import { ApolloError } from '@apollo/client';
import {
  faCheck,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { Stack, IconButton } from '@mui/material';
import {
  PickerChangeHandlerContext,
  TimeValidationError,
} from '@mui/x-date-pickers';
import {
  addDays,
  differenceInMinutes,
  format,
  isAfter,
  parse,
  parseISO,
} from 'date-fns';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { TimesheetItem } from '../../TimesheetList';
import { getTimesheetReport } from '../../util';

import { TickIcon } from '@/assets/icons';
import EditTimeIcon from '@/assets/icons/EditTimeIcon';
import Alert from '@/components/Alert';
import { SubmitErrorAlertProps } from '@/components/Alerts';
import IconicButton from '@/components/IconicButton';
import Button from '@/components/Button';
import ShiftTimePicker from '@/components/ShiftTimePicker';
import { UNKNOWN_ERROR_TEXT } from '@/constants/text';
import Form from '@/form';
import TextField from '@/form/TextField';
import {
  GetJobDocument,
  useCreateTimesheetMutation,
  useSaveTimesheetMutation,
} from '@/graphql';
import PermissionComponent from '@/routes/PermissionComponent';
import styled from '@/styles';
import { Maybe } from '@/types';
import { GetJobQuery, Scalars } from '@/types/graphql';
import { getFormattedDateInTimeZone, isDateInvalid } from '@/util/date';
import { handleGraphQLError } from '@/util/error';
import { axiosRubyClient } from '@/util/axios/axiosClient';
import CheckinModal from '../modals/CheckinModal';
import CheckoutModal from '../modals/CheckoutModal';

interface InlineTimesheetViewProps {
  timesheet?: TimesheetItem;
  allowEdit?: boolean;
  job: GetJobQuery['job'];
  workerInfo: any;
  setInlineEditing: (value: boolean) => void;
  totalTime?: string | number;
  phoneOnly: boolean;
  onTotalTimeChange?: (totalTime: number | null) => void;
  isMissingTimesheet?: boolean;
  isMissingCheckout?: boolean;
}

type InlineTimesheetFormattedFormValues = {
  timesheetId: string;
  breakMinutes?: number;
  checkinAt?: string;
  checkoutAt?: string;
};

const TimeLabel = styled('span', {
  fontSize: '10px',
  fontWeight: '400',
  color: '#01483D',
  textTransform: 'uppercase',
});

const Time = styled('span', {
  color: '#1C1C1C',
  textAlign: 'center',
  fontFamily: 'Manrope',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: '700',
  lineHeight: '22px',
  textTransform: 'uppercase',
});

const TimeInput = {
  borderColor: '#FFF',
  borderRadius: '0px',
  borderBottom: '1px solid #44A735',
  width: '90px',
  minWidth: '135px',
};

const TimeInputText = {
  padding: '4px 2px',
  fontWeight: 700,
  fontSize: '16px !important',
  color: '#01483D',
};

const TextInput = {
  borderRadius: '0px',
  borderBottom: '1px solid #44A735',
  padding: '0px',
  height: '40px',
  fontWeight: 700,
  fontSize: '16px',
  color: '#01483D',
};

type InlineFormValues = {
  modifiedBreakMinutes: string;
  modifiedCheckinAt: string;
  modifiedCheckoutAt: string;
};

const InlineTimesheetView = (props: InlineTimesheetViewProps) => {
  const {
    timesheet,
    job,
    workerInfo,
    allowEdit = true,
    setInlineEditing,
    totalTime,
    phoneOnly,
    onTotalTimeChange,
    isMissingTimesheet,
    isMissingCheckout,
  } = props;

  // Find the jobWorker to check if worker is marked as no-show or dismissed
  const jobWorker = job.jobWorkers.find(
    (jw) =>
      jw.worker.id === workerInfo.id || jw.id === timesheet?.jobWorker?.id,
  );
  const isNoShow = jobWorker ? !jobWorker.showed : false;
  const isDismissed = jobWorker ? !!jobWorker.dismissType : false;
  const isRemoved = jobWorker ? jobWorker.status === 'DISMISSED' : false;
  const shift = job.shifts[0];
  const shiftStartAt = timesheet?.shift?.startAt || shift?.startAt;
  const isWithin8HoursOfShiftStart = shiftStartAt
    ? differenceInMinutes(new Date(shiftStartAt), new Date()) <= 480
    : true;

  const initialValues: InlineFormValues = useMemo((): InlineFormValues => {
    const _checkIn = getTimesheetReport(
      timesheet?.approvedCheckinAt || null,
      timesheet?.modifiedCheckinAt || null,
      timesheet?.reportedCheckinAt || null,
      timesheet?.checkinAt || null,
    );
    const _checkOut = getTimesheetReport(
      timesheet?.approvedCheckoutAt || null,
      timesheet?.modifiedCheckoutAt || null,
      timesheet?.reportedCheckoutAt || null,
      timesheet?.checkoutAt || null,
    );

    const _breakMinutes =
      (timesheet?.approvedCheckoutAt && timesheet?.approvedBreakMinutes) ??
      (timesheet?.modifiedCheckoutAt && timesheet?.modifiedBreakMinutes) ??
      timesheet?.reportedBreakMinutes ??
      0;

    return {
      modifiedBreakMinutes: _breakMinutes ?? '',
      modifiedCheckinAt: _checkIn ? format(_checkIn, 'HH:mm') : '',
      modifiedCheckoutAt: _checkOut ? format(_checkOut, 'HH:mm') : '',
    };
  }, [timesheet]);

  const [isEditing, setIsEditing] = useState(false);
  const [editingMode, setEditingMode] = useState<
    'none' | 'all' | 'checkin' | 'checkout'
  >('none');
  const [formValues, setFormValues] = useState<InlineFormValues>(initialValues);
  const [localCheckIn, setLocalCheckIn] = useState<Date | null>(null);
  const [localCheckOut, setLocalCheckOut] = useState<Date | null>(null);
  const [localBreakMinutes, setLocalBreakMinutes] = useState<number | null>(
    null,
  );
  const [localCheckInTime, setLocalCheckInTime] = useState<string | null>(null);
  const [localCheckOutTime, setLocalCheckOutTime] = useState<string | null>(
    null,
  );
  const [showCheckinModal, setShowCheckinModal] = useState(false);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [isCheckoutSubmitting, setIsCheckoutSubmitting] = useState(false);

  useEffect(() => {
    setFormValues(initialValues);
  }, [initialValues]);

  const [submitError, setSubmitError] =
    useState<Maybe<SubmitErrorAlertProps>>(null);

  useEffect(() => {
    if (isEditing && !allowEdit) {
      setIsEditing(false);
      setInlineEditing(false);
      setSubmitError(null);
      setEditingMode('none');
    }
  }, [allowEdit]);

  const handleOnErrorTimesheetMutation = (error: ApolloError) => {
    handleGraphQLError(error, {
      INVALID_DATES: () => {
        setSubmitError({
          title: 'Invalid shift times',
          description: 'description TEST',
        });
      },
      WORKING_TIME_TOO_SHORT: ({ message }) => {
        setSubmitError({
          title: 'Approved time must be longer',
          description: message,
        });
      },
      BREAK_TIME_TOO_LONG: ({ message }) => {
        setSubmitError({
          title: 'Break time must be short',
          description: message,
        });
      },
      SHIFT_TOO_LONG: () => {
        setSubmitError({
          title: 'Shift length too long',
          description: 'Shifts cannot exceed 24 hours.',
        });
      },
      all: () => {
        setSubmitError({
          description: UNKNOWN_ERROR_TEXT,
        });
      },
    });
  };

  const handleSaveTimesheet = async (
    timesheetFormattedFormValues: InlineTimesheetFormattedFormValues,
  ) => {
    const { checkinAt, checkoutAt, breakMinutes, timesheetId } =
      timesheetFormattedFormValues;

    const variables: any = {
      timesheetId,
      tipAmount: timesheet?.tipAmount || 0,
      ratingComment: '',
    };

    if (typeof checkinAt !== 'undefined') {
      variables.modifiedCheckinAt = checkinAt;
    }
    if (typeof checkoutAt !== 'undefined') {
      variables.modifiedCheckoutAt = checkoutAt;
    }
    if (typeof breakMinutes !== 'undefined' && !Number.isNaN(breakMinutes)) {
      variables.modifiedBreakMinutes = breakMinutes;
    }

    await saveInlineTimesheet({
      variables,
    });
  };

  const [saveInlineTimesheet] = useSaveTimesheetMutation({
    refetchQueries: [{ query: GetJobDocument, variables: { jobId: job.id } }],
    onCompleted: () => {
      setIsEditing(false);
      setInlineEditing(false);
    },
    onError: handleOnErrorTimesheetMutation,
  });
  const [createInlineTimesheet] = useCreateTimesheetMutation({
    onError: handleOnErrorTimesheetMutation,
  });

  const checkIn = getTimesheetReport(
    timesheet?.approvedCheckinAt || null,
    timesheet?.modifiedCheckinAt || null,
    timesheet?.reportedCheckinAt || null,
    timesheet?.checkinAt || null,
    job.address.timezone,
  );

  const checkOut = getTimesheetReport(
    timesheet?.approvedCheckoutAt || null,
    timesheet?.modifiedCheckoutAt || null,
    timesheet?.reportedCheckoutAt || null,
    timesheet?.checkoutAt || null,
    job.address.timezone,
  );

  // Calculate local total time using local state when available
  const calculateLocalTotalTime = () => {
    let currentCheckIn = localCheckIn || checkIn;
    let currentCheckOut = localCheckOut || checkOut;

    // If we have local time strings, create date objects from them
    if (localCheckInTime) {
      const [hours, minutes] = localCheckInTime.split(':').map(Number);
      const todayForCalc = new Date();
      currentCheckIn = new Date(
        todayForCalc.getFullYear(),
        todayForCalc.getMonth(),
        todayForCalc.getDate(),
        hours,
        minutes,
        0,
      );
    }

    if (localCheckOutTime) {
      const [hours, minutes] = localCheckOutTime.split(':').map(Number);
      const todayForCalc2 = new Date();
      currentCheckOut = new Date(
        todayForCalc2.getFullYear(),
        todayForCalc2.getMonth(),
        todayForCalc2.getDate(),
        hours,
        minutes,
        0,
      );
    }

    const currentBreakMinutes =
      localBreakMinutes ??
      (timesheet?.approvedCheckoutAt && timesheet?.approvedBreakMinutes) ??
      (timesheet?.modifiedCheckoutAt && timesheet?.modifiedBreakMinutes) ??
      timesheet?.reportedBreakMinutes ??
      0;

    if (!currentCheckIn || !currentCheckOut) return null;

    // Calculate net time in minutes
    const netMinutes =
      differenceInMinutes(currentCheckOut, currentCheckIn) -
      currentBreakMinutes;
    const netHours = netMinutes / 60;

    // Truncate to 2 decimal places
    return netHours > 0 ? Math.floor(netHours * 100) / 100 : netHours;
  };

  const localTotalTime = calculateLocalTotalTime();

  // Helper function to format time display without timezone conversion
  const formatTimeDisplay = (timeString: string) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return format(date, 'h:mm a');
  };

  // Notify parent component when local total time changes
  useEffect(() => {
    if (onTotalTimeChange) {
      onTotalTimeChange(localTotalTime);
    }
  }, [localTotalTime, onTotalTimeChange]);

  const editTimesheet = () => {
    if (!isEditing) {
      // When starting to edit, populate form with current values only if they exist
      const currentCheckIn = localCheckIn || checkIn;
      const currentCheckOut = localCheckOut || checkOut;
      const currentBreakMinutes =
        localBreakMinutes ??
        (timesheet?.approvedCheckoutAt && timesheet?.approvedBreakMinutes) ??
        (timesheet?.modifiedCheckoutAt && timesheet?.modifiedBreakMinutes) ??
        timesheet?.reportedBreakMinutes ??
        0;

      setFormValues({
        modifiedCheckinAt: currentCheckIn
          ? format(currentCheckIn, 'HH:mm')
          : '',
        modifiedCheckoutAt: currentCheckOut
          ? format(currentCheckOut, 'HH:mm')
          : '',
        modifiedBreakMinutes:
          currentBreakMinutes > 0 ? currentBreakMinutes.toString() : '',
      });
    }

    setInlineEditing(!isEditing);
    setIsEditing((isEditing) => !isEditing);
    setEditingMode((prev) => (prev === 'all' && isEditing ? 'none' : 'all'));
  };

  const startEditCheckin = () => {
    // Always allow modal to open, regardless of allowEdit permission
    console.log('workerInfo for checkin:', workerInfo);
    console.log('Full workerInfo keys:', Object.keys(workerInfo));
    console.log('job object:', job);
    console.log('job.jobWorkers:', job.jobWorkers);
    setShowCheckinModal(true);
  };

  const handleCheckinConfirm = async (checkinTime: string) => {
    setShowCheckinModal(false);

    console.log('Received checkinTime:', checkinTime);

    try {
      // Send time components separately to avoid timezone conversion issues
      const [checkinHours, checkinMinutes] = checkinTime.split(':').map(Number);
      const today = new Date();
      const dateString = format(today, 'yyyy-MM-dd');

      console.log('Selected time:', checkinTime);
      console.log('Job timezone:', job.address.timezone);
      console.log('Date string:', dateString);
      console.log('Hours:', checkinHours, 'Minutes:', checkinMinutes);

      // Call axios endpoint with separate time components
      await axiosRubyClient.post('/api/admin/timesheets/override_checkin', {
        jobId: job.id,
        workerId: workerInfo.id,
        date: dateString,
        time: checkinTime,
        timezone: job.address.timezone,
      });

      // Store the exact time string for display
      setLocalCheckInTime(checkinTime);
      console.log('Set localCheckInTime to:', checkinTime);

      // Also store the date object for consistency
      const [hoursForDate, minutesForDate] = checkinTime.split(':').map(Number);
      const todayForCheckin = new Date();
      const checkinDate = new Date(
        todayForCheckin.getFullYear(),
        todayForCheckin.getMonth(),
        todayForCheckin.getDate(),
        hoursForDate,
        minutesForDate,
        0,
      );
      setLocalCheckIn(checkinDate);
    } catch (error) {
      console.error('Error saving check-in:', error);
    }
  };

  const startEditCheckout = () => {
    // Always allow modal to open, regardless of allowEdit permission
    console.log('=== CHECKOUT BUTTON CLICKED ===');
    console.log('workerInfo for checkout:', workerInfo);
    console.log('showCheckoutModal before:', showCheckoutModal);
    setShowCheckoutModal(true);
    console.log('showCheckoutModal set to true');
    console.log('================================');
  };

  const handleCheckoutConfirm = async (
    checkoutTime: string,
    breakMinutes: number,
  ) => {
    console.log('=== HANDLE CHECKOUT CONFIRM CALLED ===');
    console.log('checkoutTime:', checkoutTime);
    console.log('breakMinutes:', breakMinutes);
    setShowCheckoutModal(false);
    setIsCheckoutSubmitting(true);

    try {
      // Send time components separately to avoid timezone conversion issues
      const [checkoutHours, checkoutMinutes] = checkoutTime
        .split(':')
        .map(Number);
      const today = new Date();
      const dateString = format(today, 'yyyy-MM-dd');

      console.log('Selected checkout time:', checkoutTime);
      console.log('Selected break minutes:', breakMinutes);
      console.log('Job timezone:', job.address.timezone);
      console.log('Date string:', dateString);
      console.log('Hours:', checkoutHours, 'Minutes:', checkoutMinutes);

      // Call axios endpoint with timesheet ID
      console.log('=== MAKING AXIOS CALL ===');
      console.log('Endpoint: /api/admin/timesheets/override_checkout');
      console.log('Payload:', {
        jobId: job.id,
        workerId: workerInfo.id,
        date: dateString,
        time: checkoutTime,
        breakMinutes,
        timezone: job.address.timezone,
      });

      await axiosRubyClient.post('/api/admin/timesheets/override_checkout', {
        jobId: job.id,
        workerId: workerInfo.id,
        date: dateString,
        time: checkoutTime,
        breakMinutes,
        timezone: job.address.timezone,
      });

      console.log('=== AXIOS CALL SUCCESSFUL ===');

      // Store the exact time string for display
      setLocalCheckOutTime(checkoutTime);
      setLocalBreakMinutes(breakMinutes);
      console.log('Set localCheckOutTime to:', checkoutTime);
      console.log('Set localBreakMinutes to:', breakMinutes);

      // Also store the date object for consistency
      const [hoursForDate, minutesForDate] = checkoutTime
        .split(':')
        .map(Number);
      const todayForCheckout = new Date();
      const checkoutDate = new Date(
        todayForCheckout.getFullYear(),
        todayForCheckout.getMonth(),
        todayForCheckout.getDate(),
        hoursForDate,
        minutesForDate,
        0,
      );
      setLocalCheckOut(checkoutDate);

      // Refresh the page after successful checkout
      console.log('Refreshing page after successful checkout...');
      window.location.reload();
    } catch (error) {
      console.error('Error saving check-out:', error);
      setIsCheckoutSubmitting(false);
    } finally {
    }
  };

  const getParsedDateRange = (
    modifiedCheckinAt: string,
    modifiedCheckoutAt: string,
    shiftStartAt: string,
  ) => {
    const startAt = parse(modifiedCheckinAt, 'HH:mm', parseISO(shiftStartAt));
    const endAt = parse(modifiedCheckoutAt, 'HH:mm', parseISO(shiftStartAt));

    return { startAt, endAt };
  };

  const handleFormValuesChange = (
    fieldContext,
    fieldId: keyof InlineFormValues,
  ) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      [fieldId]: fieldContext.value,
    }));
  };

  const handleTimeChange = (
    time: Dayjs,
    event: PickerChangeHandlerContext<TimeValidationError>,
    key: string,
  ) => {
    const value = time ? time?.format('HH:mm') : '';
    setFormValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = useCallback(
    async ({ modifiedBreakMinutes }: InlineFormValues) => {
      const { modifiedCheckinAt, modifiedCheckoutAt } = formValues;
      setSubmitError(null);

      let timesheetId = timesheet?.id;

      if (
        editingMode !== 'checkin' &&
        editingMode !== 'checkout' &&
        job.status !== 'COMPLETED'
      ) {
        if (modifiedCheckinAt !== '' && modifiedCheckoutAt === '') {
          const { startAt } = getParsedDateRange(
            modifiedCheckinAt,
            modifiedCheckoutAt || modifiedCheckinAt,
            timesheet?.shift.startAt || shift.startAt,
          );
          const checkinAt = getFormattedDateInTimeZone(
            startAt,
            job.address.timezone,
          ).toISOString();

          // Use specific API endpoint for check-in override
          // Call axios endpoint with separate time components
          const today = new Date();
          const dateString = format(today, 'yyyy-MM-dd');
          await axiosRubyClient.post('/api/admin/timesheets/override_checkin', {
            jobId: job.id,
            workerId: workerInfo.id,
            date: dateString,
            time: modifiedCheckinAt,
            timezone: job.address.timezone,
          });
          setIsEditing(false);
          setInlineEditing(false);
          setEditingMode('none');
          setLocalCheckInTime(modifiedCheckinAt);
          return;
        }
      }

      if (!timesheetId) {
        const createResp = await createInlineTimesheet({
          variables: {
            jobId: job.id,
            workerId: workerInfo.id,
          },
        });

        if (createResp?.data?.timesheetCreate.timesheet.id) {
          timesheetId = createResp?.data?.timesheetCreate.timesheet.id;
        } else {
          return;
        }
      }

      // If invoked from dedicated checkin/checkout buttons, skip validations
      if (editingMode === 'checkin' || editingMode === 'checkout') {
        const payload: InlineTimesheetFormattedFormValues = {
          timesheetId,
        };

        if (editingMode === 'checkin' && modifiedCheckinAt !== '') {
          const { startAt } = getParsedDateRange(
            modifiedCheckinAt,
            modifiedCheckoutAt || modifiedCheckinAt,
            timesheet?.shift.startAt || shift.startAt,
          );
          const checkinAt = getFormattedDateInTimeZone(
            startAt,
            job.address.timezone,
          ).toISOString();

          // Use specific API endpoint for check-in override
          await axiosRubyClient.post('/api/admin/timesheets/override_checkin', {
            timesheetId,
            checkinAt,
          });
          setIsEditing(false);
          setInlineEditing(false);
          setEditingMode('none');
          setLocalCheckIn(parseISO(checkinAt));
          return;
        }
        console.log('editingMode:', editingMode);
        console.log('modifiedCheckoutAt:', modifiedCheckoutAt);
        if (editingMode === 'checkout' && modifiedCheckoutAt !== '') {
          console.log('inside checkout if');
          const { endAt } = getParsedDateRange(
            modifiedCheckinAt || modifiedCheckoutAt,
            modifiedCheckoutAt,
            timesheet?.shift.startAt || shift.startAt,
          );
          const checkoutAt = getFormattedDateInTimeZone(
            endAt,
            job.address.timezone,
          ).toISOString();

          // Use specific API endpoint for check-out override
          const requestBody: any = {
            timesheetId,
            checkoutAt,
          };

          if (modifiedBreakMinutes !== '') {
            requestBody.breakMinutes = Number(modifiedBreakMinutes);
          }

          await axiosRubyClient.post(
            '/api/admin/timesheets/override_checkout',
            requestBody,
          );
          setIsEditing(false);
          setInlineEditing(false);
          setEditingMode('none');
          setLocalCheckOut(parseISO(checkoutAt));
          if (requestBody.breakMinutes !== undefined) {
            setLocalBreakMinutes(requestBody.breakMinutes);
          }
          return;
        }

        await handleSaveTimesheet(payload);
        return;
      }

      // If only check-in provided (no checkout and no break), use the same flow as check-in button (axios override_checkin)
      if (
        modifiedCheckinAt !== '' &&
        modifiedCheckoutAt === '' &&
        modifiedBreakMinutes === ''
      ) {
        try {
          const today = new Date();
          const dateString = format(today, 'yyyy-MM-dd');

          await axiosRubyClient.post('/api/admin/timesheets/override_checkin', {
            jobId: job.id,
            workerId: workerInfo.id,
            date: dateString,
            time: modifiedCheckinAt,
            timezone: job.address.timezone,
          });

          // Update local state for immediate UI feedback
          setLocalCheckInTime(modifiedCheckinAt);
          const [hoursForDate, minutesForDate] = modifiedCheckinAt
            .split(':')
            .map(Number);
          const todayForCheckin = new Date();
          const checkinDate = new Date(
            todayForCheckin.getFullYear(),
            todayForCheckin.getMonth(),
            todayForCheckin.getDate(),
            hoursForDate,
            minutesForDate,
            0,
          );
          setLocalCheckIn(checkinDate);

          setIsEditing(false);
          setInlineEditing(false);
          setEditingMode('none');
          return;
        } catch (error) {
          console.error('Error saving check-in via inline edit:', error);
        }
      }

      // Original validations for full edits
      if (
        modifiedCheckinAt === '' &&
        modifiedCheckoutAt === '' &&
        modifiedBreakMinutes === ''
      ) {
        setIsEditing(false);
        setInlineEditing(false);
        return;
      }

      let { startAt, endAt } = getParsedDateRange(
        modifiedCheckinAt,
        modifiedCheckoutAt,
        timesheet?.shift.startAt || shift.startAt,
      );

      if (isDateInvalid(startAt) || isDateInvalid(endAt)) {
        const timeLabel = isDateInvalid(startAt)
          ? 'a start time'
          : 'an end time';

        setSubmitError({
          title: 'Shift times are invalid',
          description: `Enter ${timeLabel} to approve.`,
        });
        return;
      }

      if (isAfter(startAt, endAt)) {
        endAt = addDays(endAt, 1);
      }

      const timesheetFormattedFormValues: InlineTimesheetFormattedFormValues = {
        timesheetId,
        breakMinutes: Number(modifiedBreakMinutes),
        checkinAt: getFormattedDateInTimeZone(
          startAt,
          job.address.timezone,
        ).toISOString(),
        checkoutAt: getFormattedDateInTimeZone(
          endAt,
          job.address.timezone,
        ).toISOString(),
      };

      await handleSaveTimesheet(timesheetFormattedFormValues);
    },
    [formValues, timesheet?.id, editingMode],
  );

  return (
    <Stack gap={0.5}>
      {submitError && (
        <Alert
          description={submitError.description}
          icon={faExclamationTriangle}
          status={'warning'}
          style={{ padding: '10px' }}
        />
      )}

      <CheckinModal
        open={showCheckinModal}
        onClose={() => setShowCheckinModal(false)}
        onConfirm={handleCheckinConfirm}
        workerInfo={{
          id: workerInfo.workerId || workerInfo.id,
          firstName: workerInfo.user?.firstName || '',
          lastName: workerInfo.user?.lastName || '',
          rating: workerInfo.averageRating,
          reviewCount: workerInfo.jobsRatingCount,
          avatar: workerInfo.avatarUrl,
        }}
        currentCheckinTime={localCheckIn || checkIn}
        timezone={job.address.timezone}
      />

      <CheckoutModal
        open={showCheckoutModal}
        onClose={() => setShowCheckoutModal(false)}
        onConfirm={handleCheckoutConfirm}
        workerInfo={{
          id: workerInfo.workerId || workerInfo.id,
          firstName: workerInfo.user?.firstName || '',
          lastName: workerInfo.user?.lastName || '',
          rating: workerInfo.averageRating,
          reviewCount: workerInfo.jobsRatingCount,
          avatar: workerInfo.avatarUrl,
        }}
        currentCheckoutTime={localCheckOut || checkOut}
        currentBreakMinutes={
          localBreakMinutes ??
          (timesheet?.approvedCheckoutAt && timesheet?.approvedBreakMinutes) ??
          (timesheet?.modifiedCheckoutAt && timesheet?.modifiedBreakMinutes) ??
          timesheet?.reportedBreakMinutes ??
          0
        }
        timezone={job.address.timezone}
      />
      <Form
        data-testid="inline-timesheet-form"
        id="inline-timesheet-form"
        initialValues={formValues}
        onSubmit={handleSubmit}
      >
        <Stack
          alignItems={'center'}
          flexDirection={'row'}
          justifyContent={'space-between'}
        >
          <Stack
            flexDirection={'row'}
            gap={isEditing ? '10px' : '30px'}
            padding={'4px'}
          >
            <Stack>
              <TimeLabel>check In</TimeLabel>
              {!isEditing ? (
                localCheckInTime ? (
                  <Time>{formatTimeDisplay(localCheckInTime)}</Time>
                ) : localCheckIn || checkIn ? (
                  <Time>{format(localCheckIn || checkIn, 'p')}</Time>
                ) : !isMissingTimesheet &&
                  !isNoShow &&
                  !isDismissed &&
                  !isRemoved &&
                  isWithin8HoursOfShiftStart ? (
                  <PermissionComponent id="inline-checkin-btn">
                    <Button
                      a11yLabel="Check-in"
                      id="inline-checkin-btn"
                      label="Check-in"
                      status="success"
                      type="button"
                      css={{
                        display: 'flex',
                        height: '28px',
                        padding: '0 16px',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '4px',
                        alignSelf: 'stretch',
                        borderRadius: '4px',
                        background: '#45A735',
                        color: '#FFF',
                        textAlign: 'center',
                        fontFamily: 'Manrope',
                        fontSize: '12px',
                        fontStyle: 'normal',
                        fontWeight: 700,
                        lineHeight: '16px',
                        border: 'none',
                        '&:hover': {
                          backgroundColor: '#45A735',
                          color: '#FFF',
                          borderColor: '#45A735',
                        },
                        '&:active': {
                          backgroundColor: '#45A735',
                          color: '#FFF',
                          borderColor: '#45A735',
                        },
                        '&:focus-visible': {
                          boxShadow: '0 0 0 2px rgba(69,167,53,0.3)',
                        },
                      }}
                      onClick={startEditCheckin}
                    />
                  </PermissionComponent>
                ) : (
                  <Time>-</Time>
                )
              ) : (
                <ShiftTimePicker
                  onChange={(time, event, key) =>
                    handleTimeChange(time, event, 'modifiedCheckinAt')
                  }
                  name="modifiedCheckinAt"
                  id="modifiedCheckinAt"
                  inputStyle={TimeInput}
                  defaultValue={
                    formValues.modifiedCheckinAt
                      ? dayjs(formValues.modifiedCheckinAt, 'HH:mm')
                      : undefined
                  }
                  timeSteps={{ hours: 1, minutes: 1, seconds: 1 }}
                />
              )}
            </Stack>

            <Stack>
              <TimeLabel>check Out</TimeLabel>
              {!isEditing ? (
                localCheckOutTime ? (
                  <Time>{formatTimeDisplay(localCheckOutTime)}</Time>
                ) : localCheckOut || checkOut ? (
                  <Time>{format(localCheckOut || checkOut, 'p')}</Time>
                ) : (localCheckIn || checkIn) &&
                  !isMissingCheckout &&
                  !isNoShow &&
                  !isDismissed &&
                  !isRemoved ? (
                  <PermissionComponent id="inline-checkout-btn">
                    <Button
                      a11yLabel="Check-out"
                      id="inline-checkout-btn"
                      label="Check-out"
                      status="success"
                      type="button"
                      css={{
                        display: 'flex',
                        height: '28px',
                        padding: '0 16px',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '4px',
                        alignSelf: 'stretch',
                        borderRadius: '4px',
                        background: '#45A735',
                        color: '#FFF',
                        textAlign: 'center',
                        fontFamily: 'Manrope',
                        fontSize: '12px',
                        fontStyle: 'normal',
                        fontWeight: 700,
                        lineHeight: '16px',
                        border: 'none',
                        '&:hover': {
                          backgroundColor: '#45A735',
                          color: '#FFF',
                          borderColor: '#45A735',
                        },
                        '&:active': {
                          backgroundColor: '#45A735',
                          color: '#FFF',
                          borderColor: '#45A735',
                        },
                        '&:focus-visible': {
                          boxShadow: '0 0 0 2px rgba(69,167,53,0.3)',
                        },
                      }}
                      onClick={startEditCheckout}
                    />
                  </PermissionComponent>
                ) : (
                  <Time>-</Time>
                )
              ) : (
                <ShiftTimePicker
                  onChange={(time, event, key) =>
                    handleTimeChange(time, event, 'modifiedCheckoutAt')
                  }
                  name="modifiedCheckoutAt"
                  id="modifiedCheckoutAt"
                  inputStyle={TimeInput}
                  defaultValue={
                    formValues.modifiedCheckoutAt
                      ? dayjs(formValues.modifiedCheckoutAt, 'HH:mm')
                      : undefined
                  }
                  timeSteps={{ hours: 1, minutes: 1, seconds: 1 }}
                />
              )}
            </Stack>
            <Stack>
              <TimeLabel>Break</TimeLabel>
              <Time>
                {!isEditing ? (
                  localCheckOut || checkOut ? (
                    `${localBreakMinutes ?? (timesheet?.approvedCheckoutAt && timesheet?.approvedBreakMinutes) ?? (timesheet?.modifiedCheckoutAt && timesheet?.modifiedBreakMinutes) ?? timesheet?.reportedBreakMinutes ?? 0} Min`
                  ) : (
                    '-'
                  )
                ) : editingMode === 'all' || editingMode === 'checkout' ? (
                  <TextField
                    callback={(fieldContext) =>
                      handleFormValuesChange(
                        fieldContext,
                        'modifiedBreakMinutes',
                      )
                    }
                    displayType="block"
                    fieldId="modifiedBreakMinutes"
                    max="90"
                    min="0"
                    placeholder="0 Min"
                    showFocusCover={false}
                    style={TextInput}
                    type="number"
                    width="60px"
                  />
                ) : (
                  <>
                    {localCheckOut || checkOut
                      ? (localBreakMinutes ??
                          (timesheet?.approvedCheckoutAt &&
                            timesheet?.approvedBreakMinutes) ??
                          (timesheet?.modifiedCheckoutAt &&
                            timesheet?.modifiedBreakMinutes) ??
                          timesheet?.reportedBreakMinutes ??
                          0) + ' Min'
                      : '-'}
                  </>
                )}
              </Time>
            </Stack>
            {!isEditing &&
            !isCheckoutSubmitting &&
            (localTotalTime !== null || totalTime) ? (
              <Stack>
                <TimeLabel>TOTAL</TimeLabel>
                <Time>
                  {localTotalTime !== null
                    ? `${Number(localTotalTime).toFixed(2)}`
                    : typeof totalTime === 'number'
                      ? `${totalTime.toFixed(2)}`
                      : totalTime}
                </Time>
              </Stack>
            ) : null}
          </Stack>

          <Stack
            flexDirection={'row'}
            gap={'0'}
            padding={'0'}
            style={{ marginLeft: '10px' }}
          >
            {isEditing ? (
              <IconicButton
                a11yLabel="Save Timesheet"
                appearance="clear"
                customIcon={
                  <TickIcon
                    style={{
                      cursor: 'pointer',
                    }}
                  />
                }
                data-testid="save-timesheet-btn"
                icon={faCheck}
                id="save-timesheet-btn"
                size="sm"
                type="submit"
              />
            ) : (
              <PermissionComponent id="edit-timesheet-btn">
                <IconButton
                  data-testid="edit-timesheet-btn"
                  disabled={
                    !allowEdit ||
                    isMissingTimesheet ||
                    isMissingCheckout ||
                    isDismissed ||
                    isRemoved
                  }
                  id="edit-timesheet-btn"
                  style={{ padding: '1px' }}
                  onClick={editTimesheet}
                >
                  <EditTimeIcon
                    style={{
                      cursor: 'pointer',
                      opacity:
                        allowEdit &&
                        !isMissingTimesheet &&
                        !isMissingCheckout &&
                        !isDismissed &&
                        !isRemoved
                          ? 1.0
                          : 0.5,
                    }}
                  />
                </IconButton>
              </PermissionComponent>
            )}
          </Stack>
        </Stack>
      </Form>
    </Stack>
  );
};

export default InlineTimesheetView;
